import { supportApi } from './api';
import {
  Ticket,
  TicketDetail,
  Message,
  SupportCall,
  PaginatedResponse,
  TicketQueryParams,
  MessageQueryParams,
  UpdateTicketDto,
  AddMessageDto,
  EscalateTicketDto,
  VoiceTokenResponse,
  VoiceConfigResponse,
  OutboundCallInfo,
} from '../types/support';

// ============================================================================
// Helper - no transformation needed, backend uses same param names
// ============================================================================

// ============================================================================
// Admin Ticket APIs
// ============================================================================

/**
 * Get paginated list of tickets with filters
 * Backend expects: status, priority, category, escalationStatus, assignedToUserId, userId, search, page, pageSize
 */
export const getTickets = async (
  params: TicketQueryParams = {}
): Promise<PaginatedResponse<Ticket>> => {
  const response = await supportApi.get('/admin/tickets', { params });
  return response.data;
};

/**
 * Get single ticket with full details
 */
export const getTicketById = async (id: string): Promise<TicketDetail> => {
  const response = await supportApi.get(`/admin/tickets/${id}`);
  return response.data;
};

/**
 * Update ticket (status, priority, assignment)
 */
export const updateTicket = async (
  id: string,
  dto: UpdateTicketDto
): Promise<Ticket> => {
  const response = await supportApi.patch(`/admin/tickets/${id}`, dto);
  return response.data;
};

/**
 * Escalate a ticket for admin review
 */
export const escalateTicket = async (
  id: string,
  dto: EscalateTicketDto
): Promise<{ ticket: Ticket }> => {
  const response = await supportApi.post(`/admin/tickets/${id}/escalate`, dto);
  return response.data;
};

// ============================================================================
// Message APIs
// ============================================================================

/**
 * Get messages for a ticket (supports polling with since/afterMessageId)
 */
export const getMessages = async (
  ticketId: string,
  params: MessageQueryParams = {}
): Promise<PaginatedResponse<Message>> => {
  const response = await supportApi.get(`/admin/tickets/${ticketId}/messages`, {
    params,
  });
  return response.data;
};

/**
 * Add a message/reply to a ticket
 */
export const addMessage = async (
  ticketId: string,
  dto: AddMessageDto
): Promise<Message> => {
  const response = await supportApi.post(
    `/admin/tickets/${ticketId}/messages`,
    dto
  );
  return response.data;
};

// ============================================================================
// Call APIs
// ============================================================================

/**
 * Get calls for a ticket
 */
export const getCallsForTicket = async (
  ticketId: string
): Promise<SupportCall[]> => {
  const response = await supportApi.get(`/admin/tickets/${ticketId}/calls`);
  return response.data;
};

/**
 * Get all calls (paginated)
 */
export const getAllCalls = async (
  page: number = 1,
  pageSize: number = 20
): Promise<PaginatedResponse<SupportCall>> => {
  const response = await supportApi.get('/admin/calls', {
    params: { page, pageSize },
  });
  return response.data;
};

/**
 * Get outbound call info for a ticket
 */
export const getOutboundCallInfo = async (
  ticketId: string
): Promise<OutboundCallInfo> => {
  const response = await supportApi.get(`/admin/calls/outbound-info/${ticketId}`);
  return response.data;
};

/**
 * Link a call to a ticket
 */
export const linkCallToTicket = async (
  callId: string,
  ticketId: string
): Promise<SupportCall> => {
  const response = await supportApi.post(
    `/admin/calls/${callId}/link/${ticketId}`
  );
  return response.data;
};

// ============================================================================
// Voice/WebRTC APIs
// ============================================================================

/**
 * Get Twilio Voice token for WebRTC calling
 */
export const getVoiceToken = async (): Promise<VoiceTokenResponse> => {
  const response = await supportApi.get('/voice/token');
  return response.data;
};

/**
 * Get shared voice token (for team call handling)
 */
export const getSharedVoiceToken = async (): Promise<VoiceTokenResponse> => {
  const response = await supportApi.get('/voice/token/shared');
  return response.data;
};

/**
 * Get voice configuration
 */
export const getVoiceConfig = async (): Promise<VoiceConfigResponse> => {
  const response = await supportApi.get('/voice/config');
  return response.data;
};

// ============================================================================
// Agent Presence APIs
// ============================================================================

/**
 * Agent presence record
 */
export interface AgentPresence {
  agentId: string;
  identity: string;
  name: string;
  email: string;
  status: 'online' | 'busy' | 'offline';
  lastHeartbeat: string;
  currentCallSid?: string;
}

/**
 * Online agents response
 */
export interface OnlineAgentsResponse {
  agents: AgentPresence[];
  onlineCount: number;
  availableCount: number;
  busyCount: number;
}

/**
 * Register agent as online
 * Call when initializing WebRTC device
 */
export const registerAgentOnline = async (): Promise<{
  success: boolean;
  identity: string;
  message: string;
}> => {
  const response = await supportApi.post('/voice/presence/online');
  return response.data;
};

/**
 * Send agent heartbeat
 * Call periodically (every 30-60 seconds) to maintain online status
 */
export const sendAgentHeartbeat = async (): Promise<{ success: boolean }> => {
  const response = await supportApi.post('/voice/presence/heartbeat');
  return response.data;
};

/**
 * Set agent as busy (on a call)
 */
export const setAgentBusy = async (callSid?: string): Promise<{ success: boolean }> => {
  const response = await supportApi.post('/voice/presence/busy', { callSid });
  return response.data;
};

/**
 * Set agent as available (call ended)
 */
export const setAgentAvailable = async (): Promise<{ success: boolean }> => {
  const response = await supportApi.post('/voice/presence/available');
  return response.data;
};

/**
 * Unregister agent (go offline)
 * Call when logging out or closing dashboard
 */
export const unregisterAgent = async (): Promise<{
  success: boolean;
  message: string;
}> => {
  const response = await supportApi.delete('/voice/presence/offline');
  return response.data;
};

/**
 * Get all online agents
 */
export const getOnlineAgents = async (): Promise<OnlineAgentsResponse> => {
  const response = await supportApi.get('/voice/presence/agents');
  return response.data;
};

// ============================================================================
// Public Config API
// ============================================================================

/**
 * Get support configuration (public endpoint)
 * Returns the support phone number for "Call Now" buttons
 */
export const getSupportConfig = async (): Promise<{ supportPhoneNumber: string }> => {
  const response = await supportApi.get('/config');
  return response.data;
};

// ============================================================================
// Caller Lookup API
// ============================================================================

/**
 * Caller lookup response type
 */
export interface CallerLookupResponse {
  found: boolean;
  user: {
    id: number;
    name: string | null;
    email: string | null;
    phoneNumber: string;
    profileImage: string | null;
    userType: string;
    createdAt: string;
    customerRating: number | null;
    customerRatingCount: number;
  } | null;
  openTickets: Array<{
    id: string;
    title: string;
    status: string;
    priority: string;
    category: string;
    createdAt: string;
  }>;
  recentTickets: Array<{
    id: string;
    title: string;
    status: string;
    priority: string;
    category: string;
    createdAt: string;
  }>;
}

/**
 * Look up caller info by phone number
 * Used when incoming call arrives to fetch user and ticket info
 */
export const lookupCallerByPhone = async (
  phoneNumber: string
): Promise<CallerLookupResponse> => {
  const encodedPhone = encodeURIComponent(phoneNumber);
  const response = await supportApi.get(`/admin/calls/lookup/phone/${encodedPhone}`);
  return response.data;
};

// ============================================================================
// Export all as supportService
// ============================================================================

export const supportService = {
  // Tickets
  getTickets,
  getTicketById,
  updateTicket,
  escalateTicket,
  
  // Messages
  getMessages,
  addMessage,
  
  // Calls
  getCallsForTicket,
  getAllCalls,
  getOutboundCallInfo,
  linkCallToTicket,
  
  // Voice
  getVoiceToken,
  getSharedVoiceToken,
  getVoiceConfig,
  
  // Agent Presence
  registerAgentOnline,
  sendAgentHeartbeat,
  setAgentBusy,
  setAgentAvailable,
  unregisterAgent,
  getOnlineAgents,
  
  // Config
  getSupportConfig,
  
  // Caller Lookup
  lookupCallerByPhone,
};

export default supportService;
